import {
    addBusinessDays,
    definedOrDie,
    differenceInBusinessDays,
    formatIsoDate,
    indexOrDie,
    mustNeverExist,
    parseISO,
    singleOrDie,
    z,
    type Serializable,
} from "@repo/utils";
import { calculatePositionSplits } from "../../../erp/sap/patch/calculatePositionSplits.js";
import type {
    ArbitraryFieldAdjustmentContext,
    ArbitraryFieldComparisonContext,
    ArbitraryFieldProcessingContext,
} from "./types.js";
import type { PurchaseOrderComparison } from "../../helpers/po/comparison/types.js";

const AswoPurchaseOrderArbitrarySchema = z.object({
    deliveryState: z.record(
        z.string(),
        z.object({
            status: z.string(),
            count: z.number(),
        }),
    ),
});

export function processing(ctx: ArbitraryFieldProcessingContext) {
    const parsed = AswoPurchaseOrderArbitrarySchema.safeParse(ctx.purchaseOrderArbitrary);

    if (!parsed.success) {
        return;
    }

    const confirmation = indexOrDie(ctx.extractionResult.confirmations, ctx.confirmationIndex);

    const { deliveryState } = parsed.data;
    const arbitrary = (ctx.processingResult.arbitrary ??= {});

    for (let idx = 0; idx < ctx.processingResult.items.length; idx++) {
        const item = indexOrDie(ctx.processingResult.items, idx);

        switch (item.type) {
            case "new_item":
                break;
            case "missing_item": {
                const state = deliveryState[item.id];
                if (state) {
                    arbitrary[deliveryStatusArbitraryKey(idx)] = state.status;
                    arbitrary[deliveryChangeCountArbitraryKey(idx)] = state.count;
                }
                break;
            }
            case "item_confirmed": {
                const state = deliveryState[item.poItemId];
                const extraction = definedOrDie(
                    confirmation.items.find(x => x.serialNumber === item.ocSerialNumber),
                    `Extracted position with serial number "${item.ocSerialNumber}" is not found`,
                );
                if (state) {
                    arbitrary[deliveryStatusArbitraryKey(idx)] = state.status;
                    arbitrary[deliveryChangeCountArbitraryKey(idx)] = state.count;
                    for (let scheduleIdx = 0; scheduleIdx < item.confirmedDeliverySchedules.length; scheduleIdx++) {
                        const extractedSchedule = indexOrDie(extraction.deliverySchedules, scheduleIdx);
                        const mentioned =
                            (extractedSchedule.deliveryDateMentioned ?? true) ||
                            (extractedSchedule.shipmentDateMentioned ?? true);
                        arbitrary[deliveryDateMentionedArbitraryKey(idx, scheduleIdx)] = mentioned;
                        arbitrary[deliveryDateExtractedArbitraryKey(idx, scheduleIdx)] =
                            extractedSchedule.deliveryDate !== null || extractedSchedule.shipmentDate !== null;
                    }
                }
                break;
            }
            default:
                throw mustNeverExist(item);
        }
    }
}

export function comparison(ctx: ArbitraryFieldComparisonContext) {
    enrichArbitraryWithDeliveryDateState(ctx.comparison, ctx.processingResult.arbitrary ?? {}, ctx.confirmation, true);
}

export function adjust(ctx: ArbitraryFieldAdjustmentContext) {
    enrichArbitraryWithDeliveryDateState(ctx.comparison, ctx.comparison.arbitrary, ctx.confirmation, false);
}

function enrichArbitraryWithDeliveryDateState(
    comparison: PurchaseOrderComparison,
    prevArbitrary: Record<string, Serializable>,
    orderConfirmation: { date: string },
    calculateDeliveryDate: boolean,
) {
    for (let idx = 0; idx < comparison.items.length; idx++) {
        const item = indexOrDie(comparison.items, idx);
        const status = prevArbitrary[deliveryStatusArbitraryKey(idx)] as string | undefined;
        const count = prevArbitrary[deliveryChangeCountArbitraryKey(idx)] as number | undefined;

        if (status === undefined || count === undefined) {
            continue;
        }

        if (item.orderId !== null) {
            comparison.arbitrary[deliveryStatusArbitraryKey(idx)] = status;
            comparison.arbitrary[deliveryChangeCountArbitraryKey(idx)] = count;
        }

        if (item.result === "matched") {
            const lines = singleOrDie(
                calculatePositionSplits(
                    item.datapoints.scheduleLines,
                    item.datapoints.confirmations.nextValue ?? [],
                    item.datapoints.quantity.nextValue?.toNumber() ?? 0,
                    false,
                ),
            );

            let autoAccept = lines.length === 1;

            for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
                const line = indexOrDie(lines, lineIdx);

                let nextStatus =
                    (comparison.arbitrary[nextDeliveryStatusArbitraryKey(idx, lineIdx)] as string | null) ?? status;

                if (line.confirmationIdx !== null) {
                    const scheduleLine = indexOrDie(item.datapoints.scheduleLines, line.scheduleIdx);
                    const confirmation = indexOrDie(
                        item.datapoints.confirmations.nextValue ?? [],
                        line.confirmationIdx,
                    );

                    const deliveryDateMentioned = (prevArbitrary[
                        deliveryDateMentionedArbitraryKey(idx, line.confirmationIdx)
                    ] ?? true) as boolean;
                    const deliveryDateExtracted = (prevArbitrary[
                        deliveryDateExtractedArbitraryKey(idx, line.confirmationIdx)
                    ] ?? true) as boolean;

                    comparison.arbitrary[deliveryDateMentionedArbitraryKey(idx, line.confirmationIdx)] =
                        deliveryDateMentioned;
                    comparison.arbitrary[deliveryDateExtractedArbitraryKey(idx, line.confirmationIdx)] =
                        deliveryDateExtracted;

                    if (calculateDeliveryDate) {
                        if (confirmation.deliveryDate === null) {
                            confirmation.deliveryDate = deliveryDateMentioned
                                ? formatIsoDate(addBusinessDays(orderConfirmation.date, 60))
                                : scheduleLine.deliveryDate;
                            nextStatus = deliveryDateMentioned ? "T02" : status;
                        } else if (differenceInBusinessDays(parseISO(confirmation.deliveryDate), new Date()) > 90) {
                            confirmation.deliveryDate = formatIsoDate(addBusinessDays(orderConfirmation.date, 90));
                            nextStatus = "T02";
                        } else if (count >= 2) {
                            nextStatus = "T02";
                        } else {
                            nextStatus = "T01";
                        }
                    }
                    autoAccept =
                        confirmation.deliveryDate === scheduleLine.deliveryDate &&
                        confirmation.quantity.eq(scheduleLine.quantity) &&
                        autoAccept;
                } else {
                    autoAccept = false;
                }

                comparison.arbitrary[nextDeliveryStatusArbitraryKey(idx, lineIdx)] = nextStatus;
            }
            comparison.arbitrary[scheduleLinesAutoAccept(idx)] = autoAccept;
        }
    }
}

export function deliveryStatusArbitraryKey(itemIdx: number) {
    return `items.${itemIdx}.deliveryStatus`;
}

export function deliveryChangeCountArbitraryKey(itemIdx: number) {
    return `items.${itemIdx}.deliveryChangeCount`;
}

export function deliveryDateExtractedArbitraryKey(itemIdx: number, confirmationIdx: number) {
    return `items.${itemIdx}.confirmedDeliverySchedules.${confirmationIdx}.deliveryDateExtracted`;
}

export function deliveryDateMentionedArbitraryKey(itemIdx: number, confirmationIdx: number) {
    return `items.${itemIdx}.confirmedDeliverySchedules.${confirmationIdx}.deliveryDateMentioned`;
}

export function nextDeliveryStatusArbitraryKey(itemIdx: number, lineIdx: number) {
    return `items.${itemIdx}.nextScheduleLine.${lineIdx}.deliveryStatus`;
}

export function scheduleLinesAutoAccept(itemIdx: number) {
    return `items.${itemIdx}.scheduleLines.autoAccept`;
}
